<template>
  <v-dialog max-width="560px" v-model="showDialog" persistent>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-start pt-4 px-5">
        <div>
          <div class="text-h6">Create Workload</div>
          <div class="caption grey--text">
            Define a new target per location + container type. Each combination can only be created once.
          </div>
        </div>
        <v-btn icon small @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-5 pt-2">
        <v-row dense>
          <v-col cols="6">
            <DateField
              :inputProps="{
                dense: true,
                outlined: true,
                label: 'Start Date *',
                'hide-details': 'auto',
              }"
              v-model="form.tracking_start_date"
            />
          </v-col>
          <v-col cols="6">
            <MultiSelect
              label="Location *"
              :items="locationList"
              itemText="location_code"
              itemValue="id"
              outlined
              dense
              clearable
              hide-details="auto"
              v-model="form.location"
            />
          </v-col>
        </v-row>

        <div class="overline grey--text mt-4 mb-1">Container Rows</div>
        <div
          v-for="(row, index) in form.items"
          :key="index"
          class="d-flex align-center mb-2"
          style="gap: 8px"
        >
          <MultiSelect
            label="Container Type"
            :items="containerTypeList"
            itemText="type_name"
            itemValue="id"
            outlined
            dense
            clearable
            hide-details="auto"
            v-model="row.container_type"
            style="flex: 2"
          />
          <v-text-field
            label="Qty"
            type="number"
            min="1"
            outlined
            dense
            hide-details="auto"
            v-model="row.qty"
            style="flex: 1"
          />
          <v-btn icon small @click="removeRow(index)" :disabled="form.items.length === 1">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-btn text small color="primary" class="px-0" @click="addRow">
          <v-icon small class="mr-1">mdi-plus</v-icon>
          Add Row
        </v-btn>
      </v-card-text>

      <v-divider class="mt-3" />

      <v-card-actions class="px-5 py-3">
        <v-spacer />
        <v-btn depressed outlined @click="closeDialog" class="mr-2">Cancel</v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="submitting"
          :disabled="!isFormValid"
          min-width="140"
          @click="submit"
        >
          Create Workload
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DateField from "@/components/FormBaseComponents/DateField.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";

export default {
  name: "CreateWorkloadDialog",
  components: { DateField, MultiSelect },
  props: {
    value: { type: Boolean, default: false },
  },
  watch: {
    value(val) {
      if (val) {
        this.resetForm();
        this.fetchInitialData();
      }
    },
  },
  data() {
    return {
      form: {
        tracking_start_date: null,
        location: null,
        items: [{ container_type: null, qty: null }],
      },
      locationList: [],
      containerTypeList: [],
      submitting: false,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isFormValid() {
      return (
        !!this.form.tracking_start_date &&
        !!this.form.location &&
        this.form.items.every((row) => row.container_type && row.qty > 0)
      );
    },
  },
  methods: {
    resetForm() {
      this.form = {
        tracking_start_date: null,
        location: null,
        items: [{ container_type: null, qty: null }],
      };
    },
    closeDialog() {
      this.showDialog = false;
    },
    fetchInitialData() {
      this.$api.location.getLocationSelectList({ limit: "all" }).then((res) => {
        this.locationList = res.data;
      });
      this.$api.containerType.getContainerTypeList({ limit: "all" }).then((res) => {
        this.containerTypeList = res.data;
      });
    },
    addRow() {
      this.form.items.push({ container_type: null, qty: null });
    },
    removeRow(index) {
      if (this.form.items.length > 1) {
        this.form.items.splice(index, 1);
      }
    },
    submit() {
      this.submitting = true;
      const getVal = (v) => (v && typeof v === "object" ? v.id : v);
      const payload = {
        tracking_start_date: this.form.tracking_start_date,
        location: getVal(this.form.location),
        items: this.form.items.map((row) => ({
          container_type: getVal(row.container_type),
          qty: row.qty,
        })),
      };
      this.$api.workload
        .createWorkload(payload)
        .then(() => {
          this.$bus.$emit("showToastMessage", {
            message: "Workload created successfully!",
            color: "success",
          });
          this.$emit("workloadCreated");
          this.closeDialog();
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: err?.data?.non_field_errors?.[0] || err?.message || "Failed to create workload.",
            color: "error",
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>
